import { Box, Typography, Container, Paper } from '@mui/material'
import { styled } from '@mui/material/styles'
import { Link as RouterLink } from 'react-router-dom'
import mailDerpLogo from '../../logos/mailderp_logo_transparent.svg'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ShieldIcon from '@mui/icons-material/Shield'
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip'
import DeleteIcon from '@mui/icons-material/Delete'
import { alpha } from '@mui/system'
import Footer from '../components/Footer' // Add this import

// Styled components
const LogoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  padding: theme.spacing(4, 0, 2),
}))

const StyledLogo = styled('img')({
  maxWidth: '220px',
  height: 'auto',
})

const BackLink = styled(RouterLink)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  color: theme.palette.primary.main,
  textDecoration: 'none',
  marginBottom: theme.spacing(3),
  '&:hover': {
    textDecoration: 'underline',
  }
}))

const Section = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  background: alpha(theme.palette.background.paper, 0.6),
  backdropFilter: 'blur(10px)',
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
}))

const FeatureBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(2),
  gap: theme.spacing(2),
}))

function About() {
  return (
    <Container maxWidth="md" sx={{ py: 4, display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <LogoContainer>
        <StyledLogo src={mailDerpLogo} alt="MailDerp Logo" />
      </LogoContainer>

      <BackLink to="/">
        <ArrowBackIcon fontSize="small" sx={{ mr: 1 }} />
        Back to mailbox
      </BackLink>

      <Section elevation={0}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ 
          fontWeight: 600,
          background: 'linear-gradient(45deg, #646cff, #9e9eff)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          About MailDerp
        </Typography>
        
        <Typography paragraph>
          MailDerp provides free disposable email addresses that help protect your privacy online. 
          Our service allows you to create temporary email addresses instantly, without registration 
          or personal information.
        </Typography>
        
        <Typography paragraph>
          In today's digital world, your email address has become your online identity - 
          but sharing it with every website and service puts your privacy at risk and 
          can lead to unwanted spam. MailDerp offers a simple solution.
        </Typography>
      </Section>

      <Section elevation={0}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 500 }}>
          Why Use MailDerp?
        </Typography>

        <FeatureBox>
          <PrivacyTipIcon color="primary" fontSize="large" />
          <Box>
            <Typography variant="h6" gutterBottom>
              Protect Your Privacy
            </Typography>
            <Typography variant="body2">
              Keep your personal email address private. Use our disposable addresses for signups, 
              online services, and anywhere you want to maintain anonymity.
            </Typography>
          </Box>
        </FeatureBox>

        <FeatureBox>
          <ShieldIcon color="primary" fontSize="large" />
          <Box>
            <Typography variant="h6" gutterBottom>
              Avoid Spam
            </Typography>
            <Typography variant="body2">
              Stop unwanted emails from flooding your personal inbox. Our disposable addresses 
              act as a shield between you and potential spam senders.
            </Typography>
          </Box>
        </FeatureBox>

        <FeatureBox>
          <DeleteIcon color="primary" fontSize="large" />
          <Box>
            <Typography variant="h6" gutterBottom>
              Automatic Cleanup
            </Typography>
            <Typography variant="body2">
              All emails are automatically deleted after 1 hour. This ensures your temporary 
              inbox stays clean and your data doesn't linger on our servers.
            </Typography>
          </Box>
        </FeatureBox>
      </Section>

      <Section elevation={0}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 500 }}>
          How It Works
        </Typography>
        
        <Typography paragraph>
          1. You get a randomly generated email address when you visit MailDerp
        </Typography>
        <Typography paragraph>
          2. Use this address wherever you need a temporary email
        </Typography>
        <Typography paragraph>
          3. Any emails sent to this address will appear in your MailDerp inbox
        </Typography>
        <Typography paragraph>
          4. When you're done, simply close the tab - or create a new address with one click
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Note: For security reasons, all emails are automatically deleted after 1 hour.
        </Typography>
      </Section>

      <Box sx={{ mt: 'auto' }}>
        <Footer />
      </Box>
    </Container>
  )
}

export default About